import { useQuery } from '@tanstack/react-query';
import { CarStats, carStatsApi } from '../api/carStatsApi';
import { StatsPeriod } from '../api/carStatsApi';
import React, { useMemo } from 'react';

// Helper function to get the previous period based on the current period
const getPreviousPeriod = (period: StatsPeriod): StatsPeriod => {
  if (period === 'month') return 'previous-month';
  if (period === 'current-year') return 'previous-year';

  // For specific month-year format (e.g., "jan-2024")
  if (typeof period === 'string' && period.includes('-')) {
    const [monthStr, yearStr] = period.split('-');
    const year = parseInt(yearStr);
    const monthIndex = new Date(Date.parse(`${monthStr} 1, 2000`)).getMonth();

    // If January, go to December of previous year
    if (monthIndex === 0) {
      return `dec-${year - 1}` as StatsPeriod;
    }

    // Otherwise, go to previous month of same year
    const prevMonth = new Date(year, monthIndex - 1).toLocaleString('en-US', { month: 'short' }).toLowerCase();
    return `${prevMonth}-${year}` as StatsPeriod;
  }

  // Default fallback
  return 'previous-month';
};

export const useCarStats = (period: StatsPeriod = 'month', carId?: string) => {
  // Query for current period
  const currentPeriodQuery = useQuery({
    queryKey: ['car-stats', period, carId],
    queryFn: () => carStatsApi.getStats(period, carId),
    enabled: !!carId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Determine previous period
  const previousPeriod = useMemo(() => getPreviousPeriod(period), [period]);

  // Query for previous period
  const previousPeriodQuery = useQuery({
    queryKey: ['car-stats', previousPeriod, carId],
    queryFn: () => carStatsApi.getStats(previousPeriod, carId),
    enabled: !!carId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Extract current period data for this car
  const currentPeriodData: CarStats | undefined = useMemo(() => {
    return currentPeriodQuery.data?.data.find((item) => item.id === carId);
  }, [currentPeriodQuery.data, carId]);

  // Extract previous period data for this car
  const previousPeriodData: CarStats | undefined = useMemo(() => {
    return previousPeriodQuery.data?.data.find((item) => item.id === carId);
  }, [previousPeriodQuery.data, carId]);

  // Calculate delta percentage
  const deltaPercentage = useMemo(() => {
    if (!currentPeriodData || !previousPeriodData) return null;

    const currentTotal = currentPeriodData.busy_days + currentPeriodData.free_days;
    const previousTotal = previousPeriodData.busy_days + previousPeriodData.free_days;

    if (currentTotal === 0 || previousTotal === 0) return null;

    const currentBusyPercentage = (currentPeriodData.busy_days / currentTotal) * 100;
    const previousBusyPercentage = (previousPeriodData.busy_days / previousTotal) * 100;

    return Math.round((currentBusyPercentage - previousBusyPercentage) * 10) / 10;
  }, [currentPeriodData, previousPeriodData]);

  return {
    ...currentPeriodQuery,
    data: currentPeriodData,
    previousPeriodData,
    deltaPercentage,
    isLoading: currentPeriodQuery.isLoading || previousPeriodQuery.isLoading
  };
};
